import React, { useState, useEffect } from 'react';
import { useTranslations } from 'use-intl';
import TextArea from '@/UIlibrary/TextArea/TextArea';
import {
	useApproveRequestByCreditCommitteeMutation,
	ApproveRequestByCreditCommitteeInput,
	ApproveRequestByCreditCommitteeMutation,
	useRejectRequestByCreditCommitteeMutation,
	RejectRequestByCreditCommitteeMutation,
	RejectRequestByCreditCommitteeInput,
	MicrocreditRequest,
	Maybe
} from '@/graphql/__generated__/credit-requests';
import { Typography } from '@/UIlibrary/Typography/Typography';
import Button from '@/UIlibrary/Button/Button';
import toast from 'react-hot-toast';
import * as Sentry from '@sentry/react';
import logger from '@/logger';
import ConfirmModal from '@/stories/Components/ConfirmModal/ConfirmModal';
import { GRAY_COLORS } from '@/UIlibrary/globalTheme';
import Banner from '@/UIlibrary/Banner/Banner';
import { Tooltip } from '@/stories/Library/Components/Tooltip/Tooltip';
import { RequestStatusBadge } from '../RequestStatusBadge';
import { useApolloClient } from '@/providers/MultiApolloProvider';
import { SCHEMAS } from '@/apolloClient';

interface ApproveByCommitteeProps {
	requestData: Maybe<MicrocreditRequest> | null;
	refetchData: () => Promise<any>;
}

interface FieldErrors {
	committee_deliberation?: string;
	committee_requested_changes?: string;
}

const CreditCommitteeRuling: React.FC<ApproveByCommitteeProps> = ({ requestData, refetchData }) => {
	const t = useTranslations('CreditCommittee');
	const [committeeDeliberation, setCommitteeDeliberation] = useState('');
	const [committeeRequestedChanges, setCommitteeRequestedChanges] = useState('');
	const [confirmModalOpen, setConfirmModalOpen] = useState(false);
	const [rejectModalOpen, setRejectModalOpen] = useState(false);
	const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
	const creditRequests = useApolloClient(SCHEMAS.MICROCREDITS);

	useEffect(() => {
		if (requestData?.committeeRuling) {
			setCommitteeDeliberation(requestData?.committeeDeliberation || '');
			setCommitteeRequestedChanges(requestData?.committeeRequestedChanges || '');
		}
	}, [requestData]);

	const [approveByCreditCommittee, { loading: loadingApprove }] =
		useApproveRequestByCreditCommitteeMutation({
			client: creditRequests,
			onCompleted: async (data: ApproveRequestByCreditCommitteeMutation) => {
				const approveByCommitteeResult = data.approveRequestByCreditCommittee;
				if (approveByCommitteeResult?.successful) {
					logger.success('Approved by committee successfully', approveByCommitteeResult);
					toast.success(t('ApprovedByCommitteeSuccessfully'), { duration: 15000 });
					await refetchData();
				}
				if (
					approveByCommitteeResult?.mutationErrors &&
					approveByCommitteeResult.mutationErrors.length > 0
				) {
					let hasFieldErrors = false;

					approveByCommitteeResult.mutationErrors.forEach((error) => {
						if (error) {
							Sentry.captureException(error);
							switch (error.__typename) {
								case 'InvalidInput':
									logger.error('Invalid input', error.field);
									if (error.field === 'committee_deliberation') {
										setFieldErrors((prev) => ({
											...prev,
											committee_deliberation: error.message
										}));
										hasFieldErrors = true;
									} else if (error.field === 'committee_requested_changes') {
										setFieldErrors((prev) => ({
											...prev,
											committee_requested_changes: error.message
										}));
										hasFieldErrors = true;
									}
									break;
								case 'ActionFailed':
									logger.error('Action failed', error.message);
									break;
								case 'InvalidAction':
									logger.error('Invalid action', error.message);
									break;
								default:
									logger.error('Unhandled mutation error', error);
									break;
							}
						}
					});

					if (!hasFieldErrors) {
						toast.error(t('ErrorApprovingByCommittee'));
					}
				}
			},
			onError: (error) => {
				logger.error('Error passing to committee', error);
				toast.error(t('ErrorApprovingByCommittee'));
			}
		});

	const [rejectByCreditCommittee, { loading: loadingReject }] =
		useRejectRequestByCreditCommitteeMutation({
			client: creditRequests,
			onCompleted: async (data: RejectRequestByCreditCommitteeMutation) => {
				const rejectByCommitteeResult = data.rejectRequestByCreditCommittee;
				if (rejectByCommitteeResult?.successful) {
					logger.success('Rejected by committee successfully', rejectByCommitteeResult);
					toast.success(t('RejectedByCommitteeSuccessfully'), { duration: 15000 });
					await refetchData();
				}
				if (
					rejectByCommitteeResult?.mutationErrors &&
					rejectByCommitteeResult.mutationErrors.length > 0
				) {
					let hasFieldErrors = false;

					rejectByCommitteeResult.mutationErrors.forEach((error) => {
						if (error) {
							Sentry.captureException(error);
							switch (error.__typename) {
								case 'InvalidInput':
									logger.error('Invalid input', error.field);
									if (error.field === 'committee_deliberation') {
										setFieldErrors((prev) => ({
											...prev,
											committee_deliberation: error.message
										}));
										hasFieldErrors = true;
									}
									break;
								case 'ActionFailed':
									logger.error('Action failed', error.message);
									break;
								case 'InvalidAction':
									logger.error('Invalid action', error.message);
									break;
								default:
									logger.error('Unhandled mutation error', error);
									break;
							}
						}
					});

					if (!hasFieldErrors) {
						toast.error(t('ErrorRejectingByCommittee'));
					}
				}
			},
			onError: (error) => {
				logger.error('Error passing to committee', error);
				toast.error(t('ErrorRejectingByCommittee'));
			}
		});

	const handleApproveByCommittee = async () => {
		if (!committeeDeliberation || committeeDeliberation.trim() === '') {
			setFieldErrors((prev) => ({
				...prev,
				committee_deliberation: t('CommitteeDeliberationRequired')
			}));
			return;
		}
		if (!committeeRequestedChanges || committeeRequestedChanges.trim() === '') {
			setFieldErrors((prev) => ({
				...prev,
				committee_requested_changes: t('CommitteeRequestedChangesRequired')
			}));
			return;
		}

		const input: ApproveRequestByCreditCommitteeInput = {
			microcreditRequestId: requestData?.microcreditRequestId || '',
			committeeDeliberation,
			committeeRequestedChanges
		};

		await approveByCreditCommittee({ variables: { input } });
		setConfirmModalOpen(false);
	};

	const handleRejectByCommittee = async () => {
		if (!committeeDeliberation || committeeDeliberation.trim() === '') {
			setFieldErrors((prev) => ({
				...prev,
				committee_deliberation: t('CommitteeDeliberationRequired')
			}));
			return;
		}
		const input: RejectRequestByCreditCommitteeInput = {
			microcreditRequestId: requestData?.microcreditRequestId || '',
			committeeDeliberation
		};
		await rejectByCreditCommittee({ variables: { input } });
		setRejectModalOpen(false);
	};

	const committeeDeliberationHint =
		t('CommitteeDeliberationLength') + '. ' + committeeDeliberation.length + '/3400';

	const committeeRequestedChangesHint =
		t('CommitteeRequestedChangesLength') + '. ' + committeeRequestedChanges.length + '/3400';

	return (
		<div className="flex flex-col rounded-lg mt-5" style={{ backgroundColor: GRAY_COLORS.gray50 }}>
			{!requestData?.committeeRuling && (
				<Banner variant="primary" message={t('CommitteeRulingOnlyOnce')} />
			)}
			<div className="p-6">
				<div className="flex justify-center mb-5">
					{requestData?.committeeRuling && requestData.microcreditRequestStatus && (
						<RequestStatusBadge status={requestData.microcreditRequestStatus} size="md" />
					)}
				</div>
				<div className="flex flex-col gap-1 p-4 bg-white rounded-lg w-full">
					<div className="grow">
						<Typography weight="semibold">{t('CommitteeDeliberation')}</Typography>
					</div>
					<div className="w-full">
						<TextArea
							rows={10}
							value={committeeDeliberation}
							onChange={(prev) => setCommitteeDeliberation(prev.target.value)}
							placeholder={t('CommitteeDeliberationPlaceholder')}
							disabled={loadingApprove || loadingReject || !!requestData?.committeeRuling}
							error={!!fieldErrors.committee_deliberation}
							hint={fieldErrors.committee_deliberation || committeeDeliberationHint}
							maxChars={3400}
							strictMaxChars
						/>
					</div>
					<div className="grow mt-8">
						<Typography weight="semibold">{t('CommitteeRequestedChanges')}</Typography>
					</div>
					<div className="w-full">
						<TextArea
							rows={10}
							value={committeeRequestedChanges}
							onChange={(prev) => setCommitteeRequestedChanges(prev.target.value)}
							placeholder={t('CommitteeRequestedChangesPlaceholder')}
							disabled={loadingApprove || loadingReject || !!requestData?.committeeRuling}
							error={!!fieldErrors.committee_requested_changes}
							hint={fieldErrors.committee_requested_changes || committeeRequestedChangesHint}
							maxChars={3400}
							strictMaxChars
						/>
					</div>
					<div className="justify-end flex gap-4">
						<div className="grow mt-4 border-t border-gray-200 pt-4">
							<Typography weight="semibold">{t('Ruling')}</Typography>
							<div className="justify-center flex gap-4">
								<Tooltip
									content={requestData?.committeeRuling ? t('RejectButtonDisabledTooltip') : ''}
								>
									<Button
										label={t('RejectByCommittee')}
										onClick={() => setRejectModalOpen(true)}
										loading={loadingApprove || loadingReject}
										size="auto"
										color="error"
										disabled={
											loadingApprove ||
											loadingReject ||
											!committeeDeliberation ||
											committeeDeliberation.trim() === '' ||
											!!requestData?.committeeRuling
										}
									/>
								</Tooltip>
								<Tooltip
									content={requestData?.committeeRuling ? t('ApproveButtonDisabledTooltip') : ''}
								>
									<Button
										label={t('ApproveByCommittee')}
										onClick={() => setConfirmModalOpen(true)}
										loading={loadingApprove || loadingReject}
										size="auto"
										disabled={
											loadingApprove ||
											loadingReject ||
											!committeeDeliberation ||
											committeeDeliberation.trim() === '' ||
											!!requestData?.committeeRuling
										}
									/>
								</Tooltip>
							</div>
						</div>
					</div>
				</div>
				<ConfirmModal
					isOpen={confirmModalOpen}
					onClose={() => setConfirmModalOpen(false)}
					onCancel={() => setConfirmModalOpen(false)}
					onConfirm={handleApproveByCommittee}
					title={t('ConfirmApproveByCommittee')}
					cancelBtnLoading={loadingApprove}
					confirmBtnLoading={loadingApprove}
					confirmActionText={t('Approve')}
				/>
				<ConfirmModal
					isOpen={rejectModalOpen}
					onClose={() => setRejectModalOpen(false)}
					onCancel={() => setRejectModalOpen(false)}
					onConfirm={handleRejectByCommittee}
					title={t('ConfirmRejectByCommittee')}
					cancelBtnLoading={loadingReject}
					confirmBtnLoading={loadingReject}
					confirmActionText={t('Reject')}
				/>
			</div>
		</div>
	);
};

export default CreditCommitteeRuling;
